import apiClient from '../lib/api-client';
import type {
  Post,
  Comment,
  Like,
  CreatePostData,
  UpdatePostData,
  CreateCommentData,
  PaginationParams,
  PostsResponse,
  CommentsResponse,
  HashtagsResponse,
} from '../types/post';

/**
 * Service API pour les posts
 */
const postService = {
  /**
   * Créer un nouveau post
   */
  createPost: async (data: CreatePostData): Promise<{ message: string; post: Post }> => {
    const response = await apiClient.post('/posts', data);
    return response.data;
  },

  /**
   * Récupérer un post par son ID
   */
  getPost: async (id: string): Promise<{ post: Post }> => {
    const response = await apiClient.get(`/posts/${id}`);
    return response.data;
  },

  /**
   * Mettre à jour un post
   */
  updatePost: async (
    id: string,
    data: UpdatePostData
  ): Promise<{ message: string; post: Post }> => {
    const response = await apiClient.patch(`/posts/${id}`, data);
    return response.data;
  },

  /**
   * Supprimer un post
   */
  deletePost: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/posts/${id}`);
    return response.data;
  },

  /**
   * Récupérer les posts d'un utilisateur
   */
  getUserPosts: async (userId: string, params?: PaginationParams): Promise<PostsResponse> => {
    const response = await apiClient.get(`/posts/user/${userId}`, { params });
    return response.data;
  },

  /**
   * Récupérer le feed de découverte (posts publics)
   */
  getFeed: async (params?: PaginationParams): Promise<PostsResponse> => {
    const response = await apiClient.get('/posts/feed', { params });
    return response.data;
  },

  /**
   * Favoris : sauvegarder, retirer, lister
   */
  savePost: async (id: string): Promise<void> => {
    await apiClient.post(`/posts/${id}/save`);
  },

  unsavePost: async (id: string): Promise<void> => {
    await apiClient.delete(`/posts/${id}/save`);
  },

  getSavedPosts: async (params?: PaginationParams): Promise<PostsResponse> => {
    const response = await apiClient.get('/posts/saved', { params });
    return response.data;
  },

  /**
   * Liker un post
   */
  likePost: async (id: string): Promise<{ message: string; like: Like }> => {
    const response = await apiClient.post(`/posts/${id}/like`);
    return response.data;
  },

  /**
   * Retirer un like d'un post
   */
  unlikePost: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/posts/${id}/like`);
    return response.data;
  },

  /**
   * Ajouter un commentaire sur un post
   */
  addComment: async (
    postId: string,
    data: CreateCommentData
  ): Promise<{ message: string; comment: Comment }> => {
    const response = await apiClient.post(`/posts/${postId}/comments`, data);
    return response.data;
  },

  /**
   * Récupérer les commentaires d'un post
   */
  getComments: async (postId: string, params?: PaginationParams): Promise<CommentsResponse> => {
    const response = await apiClient.get(`/posts/${postId}/comments`, { params });
    return response.data;
  },

  /**
   * Réponses à un commentaire (plus anciennes d'abord)
   */
  getReplies: async (
    postId: string,
    commentId: string,
    params?: PaginationParams
  ): Promise<CommentsResponse> => {
    const response = await apiClient.get(`/posts/${postId}/comments/${commentId}/replies`, { params });
    return response.data;
  },

  /**
   * Aimer / ne plus aimer un commentaire ou une réponse
   */
  likeComment: async (postId: string, commentId: string): Promise<{ likes_count: number }> => {
    const response = await apiClient.post(`/posts/${postId}/comments/${commentId}/like`);
    return response.data;
  },

  unlikeComment: async (postId: string, commentId: string): Promise<{ likes_count: number }> => {
    const response = await apiClient.delete(`/posts/${postId}/comments/${commentId}/like`);
    return response.data;
  },

  /**
   * Supprimer un commentaire
   */
  deleteComment: async (postId: string, commentId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/posts/${postId}/comments/${commentId}`);
    return response.data;
  },

  /**
   * Récupérer les hashtags tendance
   */
  getTrendingHashtags: async (params?: { limit?: number; hours?: number }): Promise<HashtagsResponse> => {
    const response = await apiClient.get('/posts/trending/hashtags', { params });
    return response.data;
  },
};

export default postService;
